import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";

/** Device motion tiers for the public homepage scroll experience:
 *   2 = desktop (full cinematic), 1 = tablet (medium), 0 = phone (lightest),
 *  -1 = prefers-reduced-motion (everything renders static / fully revealed). */
export function useMotionTier() {
  const reduced = useReducedMotion();
  const [tier, setTier] = useState(2);
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 639px)");
    const tablet = window.matchMedia("(min-width: 640px) and (max-width: 1023px)");
    const update = () => setTier(phone.matches ? 0 : tablet.matches ? 1 : 2);
    update();
    phone.addEventListener("change", update);
    tablet.addEventListener("change", update);
    return () => {
      phone.removeEventListener("change", update);
      tablet.removeEventListener("change", update);
    };
  }, []);
  return reduced ? -1 : tier;
}

/** Tier scale factor — phone gets ~35% of desktop travel, tablet ~60%, reduced = none. */
export function tierFactor(tier) {
  return tier === 2 ? 1 : tier === 1 ? 0.6 : tier === 0 ? 0.35 : 0;
}

/** Scroll-linked parallax wrapper — transform only, GPU-friendly, user-controlled. */
export function Parallax({ children, range = 24, className = "", innerClassName = "", tier }) {
  const ref = useRef(null);
  const tf = tierFactor(tier);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const raw = useTransform(scrollYProgress, [0, 1], [range * tf, -range * tf]);
  const y = useSpring(raw, { stiffness: 70, damping: 22, mass: 0.6 });
  return (
    <div ref={ref} className={`relative ${className}`}>
      <motion.div style={tf ? { y } : undefined} className={innerClassName}>
        {children}
      </motion.div>
    </div>
  );
}

const RAIL_SECTIONS = [
  ["hero-section", "01", "HERO"],
  ["about-section", "02", "ENVIRONMENT"],
  ["products-section", "03", "MEMBER TOOLS"],
  ["live-learn-section", "04", "Live & Learn"],
  ["ai-section", "05", "AI"],
  ["rewards-section", "06", "REWARDS"],
  ["lucky-draw-section", "07", "LUCKY DRAW"],
  ["rank-section", "08", "RANKS"],
  ["certificates-section", "09", "RECOGNITION"],
  ["how-it-works-section", "10", "JOURNEY"],
  ["pillars-section", "11", "PILLARS"],
  ["trust-section", "12", "PRIVATE"],
  ["cta-section", "13", "ACCESS"],
];

/** Lucky Draw — "perpetual opportunity" center stage: layered orbital system with
 *  foreground/background separation around a dominant illuminated ∞ mark.
 *  Scroll sequence: atmosphere → back rings → main orbits → nodes → ∞ bloom → caption.
 *  Pure CSS/SVG motion. No wheel, no gambling imagery. */
export function OrbitalMark({ tier }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const still = tier < 0;
  const atmos = useTransform(scrollYProgress, [0.03, 0.22], [0, 1]);
  const backRings = useTransform(scrollYProgress, [0.1, 0.35], [0, 1]);
  const mainRings = useTransform(scrollYProgress, [0.2, 0.48], [0, 1]);
  const infO = useTransform(scrollYProgress, [0.22, 0.42], [0, 1]);
  const infS = useTransform(scrollYProgress, [0.22, 0.42], [0.9, 1]);
  return (
    <div ref={ref} className="relative h-[340px] sm:h-[420px] flex items-center justify-center" data-testid="lucky-draw-visual">
      {/* 1 · atmosphere */}
      <motion.div aria-hidden="true" className="absolute h-64 w-[420px] max-w-full rounded-full bg-[#D4AF37]/[0.07] blur-3xl" style={still ? undefined : { opacity: atmos }} />
      {[["14%", "30%"], ["80%", "24%"], ["88%", "66%"], ["10%", "70%"]].map(([x, y], i) => (
        <span key={i} aria-hidden="true" className="absolute h-1 w-1 rounded-full bg-[#D4AF37]/70 lp-particle-dot" style={{ left: x, top: y, animationDelay: `${i * 1.4}s` }} />
      ))}
      {/* 2 · back orbit pair (largest, faintest, slowest — behind the focal) */}
      <motion.svg viewBox="0 0 400 400" className="absolute inset-0 m-auto h-full max-w-[400px]" style={still ? undefined : { opacity: backRings }} aria-hidden="true">
        <g className="lp-orbit-c" style={{ transformOrigin: "200px 200px", animationDuration: "170s" }}>
          <circle cx="200" cy="200" r="186" fill="none" stroke="rgba(212,175,55,.08)" strokeWidth="1" strokeDasharray="2 10" />
        </g>
        <g className="lp-orbit-b" style={{ transformOrigin: "200px 200px", animationDuration: "135s" }}>
          <circle cx="200" cy="200" r="162" fill="none" stroke="rgba(212,175,55,.12)" strokeWidth="1" strokeDasharray="3 12" />
        </g>
      </motion.svg>
      {/* 3 · main orbital system with travelling gold nodes */}
      <motion.svg viewBox="0 0 400 400" className="absolute inset-0 m-auto h-[86%] max-w-[360px]" style={still ? undefined : { opacity: mainRings }} aria-hidden="true">
        <g className="lp-orbit-a" style={{ transformOrigin: "200px 200px", animationDuration: "46s" }}>
          <circle cx="200" cy="200" r="138" fill="none" stroke="rgba(212,175,55,.3)" strokeWidth="1" strokeDasharray="7 7" />
          <circle cx="338" cy="200" r="4" fill="#D4AF37" className="animate-pulse-dot" />
        </g>
        <g className="lp-orbit-b" style={{ transformOrigin: "200px 200px", animationDuration: "68s" }}>
          <circle cx="200" cy="200" r="106" fill="none" stroke="rgba(212,175,55,.22)" strokeWidth="1" strokeDasharray="4 9" />
          <circle cx="306" cy="200" r="3.2" fill="rgba(212,175,55,.85)" className="animate-pulse-dot" style={{ animationDelay: "0.9s" }} />
        </g>
        <g className="lp-orbit-c" style={{ transformOrigin: "200px 200px", animationDuration: "96s" }}>
          <circle cx="200" cy="200" r="76" fill="none" stroke="rgba(212,175,55,.16)" strokeWidth="1" strokeDasharray="3 8" />
          <circle cx="276" cy="200" r="2.6" fill="rgba(212,175,55,.6)" className="animate-pulse-dot" style={{ animationDelay: "1.7s" }} />
        </g>
      </motion.svg>
      {/* 4 · ∞ focal on a pool of light */}
      <motion.div className="relative z-10 text-center" style={still ? undefined : { opacity: infO, scale: infS }}>
        <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-36 w-36 rounded-full bg-[#D4AF37]/[0.14] blur-2xl" />
        <div className="relative overflow-hidden px-6 py-4">
          {!still && (
            <motion.div
              aria-hidden="true"
              initial={{ x: "-130%" }}
              whileInView={{ x: "230%" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.6, delay: 0.5, ease: "easeInOut" }}
              className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[#E9C766]/25 to-transparent pointer-events-none"
            />
          )}
          <span className="font-heading text-7xl sm:text-8xl font-bold text-gold-gradient leading-none" style={{ textShadow: "0 0 52px rgba(212,175,55,.34)" }} data-testid="lucky-draw-infinity">∞</span>
          <p className="mt-2 font-mono text-[9px] sm:text-[10px] tracking-[0.46em] uppercase text-[#D4AF37]/75">Unlimited</p>
        </div>
      </motion.div>
      {/* 5 · foreground ring arc — passes in front of the focal, instant depth cue */}
      <motion.svg viewBox="0 0 400 400" className="absolute inset-0 m-auto h-full max-w-[400px] z-20 pointer-events-none" style={still ? undefined : { opacity: mainRings }} aria-hidden="true">
        <path d="M 52 226 A 160 62 0 0 0 348 226" fill="none" stroke="rgba(212,175,55,.32)" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="10 6" />
      </motion.svg>
    </div>
  );
}

/** Certificates — formal recognition ceremony. THE CERTIFICATE DOCUMENT IS THE HERO:
 *  a portrait obsidian document with a double champagne-gold frame, PlusMaker brand
 *  header, "CERTIFICATE OF ACHIEVEMENT" title and generic recognition wording.
 *  The PM laurel seal is DEMOTED to a small embossed stamp, engraved last.
 *  Scroll story: document emerges → gains depth → frame draws → light sweeps the
 *  gold edge → content resolves → seal engraves (secondary detail). No rotation.
 *  Native CSS/SVG only. No fake credentials — purely conceptual wording. */
export function SealMark({ tier }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 92%", "end 38%"] });
  const still = tier < 0;
  const emerge = useTransform(scrollYProgress, [0.02, 0.3], [0, 1]);
  const frame = useTransform(scrollYProgress, [0.2, 0.46], [0, 1]);
  const sweepP = useTransform(scrollYProgress, [0.42, 0.68], [0, 1]);
  const c1 = useTransform(scrollYProgress, [0.34, 0.46], [0, 1]);
  const c2 = useTransform(scrollYProgress, [0.42, 0.54], [0, 1]);
  const c3 = useTransform(scrollYProgress, [0.5, 0.62], [0, 1]);
  const seal = useTransform(scrollYProgress, [0.62, 0.86], [0, 1]);
  const y = useTransform(emerge, [0, 1], [46, 0]);
  const rx = useTransform(emerge, [0, 1], [9, 0]);
  const sweepPos = useTransform(sweepP, (v) => `${130 - v * 165}% 50%`);
  const sealScale = useTransform(seal, [0, 1], [0.85, 1]);
  return (
    <div ref={ref} className="relative h-[430px] sm:h-[500px] flex items-center justify-center" data-testid="certificate-visual">
      {/* L0 — warm controlled spotlight from upper-right */}
      <motion.div aria-hidden="true" className="absolute top-[2%] right-[10%] h-56 w-56 rounded-full bg-[#E9C766]/[0.09] blur-3xl" style={still ? undefined : { opacity: emerge }} />
      <motion.div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-72 w-72 rounded-full bg-[#D4AF37]/[0.06] blur-3xl" style={still ? undefined : { opacity: emerge }} />

      <div className="relative flex flex-col items-center">
        {/* L2 — THE CERTIFICATE (hero) */}
        <motion.div
          className="relative z-10 w-[238px] h-[318px] sm:w-[272px] sm:h-[364px]"
          style={still ? undefined : { opacity: emerge, y, rotateX: rx, transformPerspective: 1000 }}
          data-testid="certificate-document"
        >
          {/* obsidian face + premium shadow */}
          <div className="absolute inset-0 rounded-[3px] border border-[#D4AF37]/25 bg-gradient-to-b from-[#0D0E12] via-[#08090C] to-[#050506] shadow-[0_34px_70px_rgba(0,0,0,.65)] overflow-hidden">
            {/* subtle brushed texture */}
            <div aria-hidden="true" className="absolute inset-0 opacity-[0.05]" style={{ background: "repeating-linear-gradient(115deg, rgba(233,199,102,.5) 0 1px, transparent 1px 7px)" }} />
            {/* single restrained light sweep across the gold edge */}
            {!still && (
              <motion.div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(105deg, transparent 42%, rgba(233,199,102,.13) 50%, transparent 58%)", backgroundSize: "240% 100%", backgroundPosition: sweepPos, mixBlendMode: "screen" }} />
            )}
          </div>
          {/* double gold frame — engraved as the document settles */}
          <svg viewBox="0 0 272 364" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" data-testid="certificate-frame">
            <motion.rect x="9" y="9" width="254" height="346" fill="none" stroke="rgba(212,175,55,.55)" strokeWidth="1" style={still ? { pathLength: 1 } : { pathLength: frame }} />
            <motion.path d="M 27 17 H 245 L 255 27 V 337 L 245 347 H 27 L 17 337 V 27 Z" fill="none" stroke="rgba(233,199,102,.4)" strokeWidth="0.8" style={still ? { pathLength: 1 } : { pathLength: frame }} />
          </svg>
          {/* document content */}
          <div className="absolute inset-0 flex flex-col items-center text-center px-7 pt-8 sm:pt-9">
            {/* brand header — PlusMaker first, small PM mark */}
            <motion.div className="flex flex-col items-center" style={still ? undefined : { opacity: c1 }} data-testid="certificate-header">
              <span className="font-heading text-[13px] font-bold tracking-[0.22em] text-white">PLUSMAKER</span>
              <span className="mt-1 font-mono text-[6px] tracking-[0.3em] text-[#D4AF37]/70">TRADE · GROW · ACHIEVE</span>
            </motion.div>
            {/* title */}
            <motion.div className="mt-4 sm:mt-5 flex flex-col items-center" style={still ? undefined : { opacity: c2 }}>
              <span className="font-heading text-[21px] sm:text-[24px] font-bold text-gold-gradient tracking-[0.12em]" data-testid="certificate-title">CERTIFICATE</span>
              <span className="mt-1.5 font-mono text-[8px] tracking-[0.44em] text-[#E9C766]">OF ACHIEVEMENT</span>
              <span className="mt-3 h-px w-14 bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" aria-hidden="true" />
            </motion.div>
            {/* generic recognition line — no fake credentials */}
            <motion.p className="mt-3.5 font-mono text-[6.5px] leading-[1.9] tracking-[0.22em] text-[#94A3B8]" style={still ? undefined : { opacity: c3 }} data-testid="certificate-recognition">
              RECOGNIZING YOUR COMMITMENT<br />IN THE PLUSMAKER TRADING JOURNEY
            </motion.p>
            {/* L3 — embossed PM seal: secondary recognition detail, engraved last */}
            <motion.div className="flex-1 flex items-center justify-center" style={still ? undefined : { opacity: seal, scale: sealScale }} data-testid="certificate-seal">
              <svg viewBox="0 0 96 96" className="h-[74px] w-[74px] sm:h-[82px] sm:w-[82px]">
                <defs>
                  <linearGradient id="sealGold" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#F5E0A0" />
                    <stop offset="0.55" stopColor="#D4AF37" />
                    <stop offset="1" stopColor="#8A6B1F" />
                  </linearGradient>
                </defs>
                <motion.path d="M 27 69 Q 11 48 27 27" fill="none" stroke="rgba(212,175,55,.75)" strokeWidth="1.2" strokeLinecap="round" style={still ? { pathLength: 1 } : { pathLength: seal }} />
                <motion.path d="M 69 69 Q 85 48 69 27" fill="none" stroke="rgba(212,175,55,.75)" strokeWidth="1.2" strokeLinecap="round" style={still ? { pathLength: 1 } : { pathLength: seal }} />
                <motion.circle cx="48" cy="48" r="26" fill="rgba(212,175,55,.05)" stroke="rgba(212,175,55,.55)" strokeWidth="1" style={still ? { pathLength: 1 } : { pathLength: seal }} />
                <motion.circle cx="48" cy="48" r="31" fill="none" stroke="rgba(212,175,55,.22)" strokeWidth="0.8" strokeDasharray="2 5" style={still ? { pathLength: 1 } : { pathLength: seal }} />
                <text x="48" y="53" textAnchor="middle" fontSize="15" fontWeight="700" letterSpacing="2" fill="url(#sealGold)" data-testid="certificate-monogram">PM</text>
              </svg>
            </motion.div>
            {/* footer motto — existing brand language */}
            <motion.span className="pb-3.5 font-mono text-[6px] tracking-[0.32em] text-[#64748B]" style={still ? undefined : { opacity: c3 }} data-testid="certificate-motto">
              DISCIPLINE BUILDS FREEDOM
            </motion.span>
          </div>
        </motion.div>

        {/* L1 — presentation pedestal + soft reflection */}
        <motion.div className="relative z-0 -mt-1 w-[300px] sm:w-[340px]" style={still ? undefined : { opacity: emerge }} data-testid="certificate-pedestal">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-[#D4AF37]/55 to-transparent" aria-hidden="true" />
          <div className="h-9 sm:h-10 border border-t-0 border-[#222530] bg-gradient-to-b from-[#14151A] to-[#07080B] rounded-b-sm" />
          <div aria-hidden="true" className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-6 w-56 bg-[#D4AF37]/10 blur-xl rounded-full" />
        </motion.div>
      </div>
    </div>
  );
}

/** Journey rail — communicates progress through the PlusMaker ecosystem.
 *  Desktop: gold rail + nodes + minimal labels. Tablet: slimmer dots only.
 *  Phone: a single gold hairline at the very top (never interferes with content). */
export function ScrollRail() {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll();
  const hairline = useSpring(scrollYProgress, { stiffness: 110, damping: 26 });

  useEffect(() => {
    const sections = RAIL_SECTIONS.map(([id]) => document.querySelector(`[data-testid="${id}"]`)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = sections.indexOf(e.target);
            if (idx >= 0) setActive(idx);
          }
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const go = (id) =>
    document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });

  return (
    <>
      {/* phone — minimal progress hairline */}
      <motion.div
        className="sm:hidden fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#B8860B] via-[#D4AF37] to-[#E9C766] origin-left z-[60] pointer-events-none"
        style={{ scaleX: hairline }}
        data-testid="scroll-progress-hairline"
      />
      {/* tablet + desktop — side rail */}
      <nav
        className="hidden sm:flex fixed left-4 lg:left-5 top-1/2 -translate-y-1/2 z-40 flex-col items-center"
        data-testid="scroll-rail"
        aria-label="Page progress"
      >
        <span className="w-px h-10 bg-gradient-to-b from-transparent to-[#D4AF37]/35" />
        <div className="relative flex flex-col items-center py-1">
          <span className="absolute top-0 bottom-0 w-px bg-[#222530]" aria-hidden="true" />
          <span
            className="absolute top-0 w-px bg-gradient-to-b from-[#D4AF37]/70 to-[#D4AF37]/20 transition-all duration-700 ease-out"
            style={{ height: `${(active / (RAIL_SECTIONS.length - 1)) * 100}%` }}
            aria-hidden="true"
          />
          {RAIL_SECTIONS.map(([id, num, label], i) => (
            <button
              key={id}
              onClick={() => go(id)}
              className="group relative flex items-center py-[7px] pl-0 pr-2"
              data-testid={`scroll-rail-node-${i}`}
              data-active={i === active}
              aria-label={`Go to ${label}`}
            >
              <span
                className={`block rounded-full transition-all duration-500 ${
                  i === active
                    ? "h-2 w-2 bg-[#D4AF37] shadow-[0_0_10px_rgba(212,175,55,.65)]"
                    : i < active
                      ? "h-1.5 w-1.5 bg-[#D4AF37]/45"
                      : "h-1.5 w-1.5 bg-[#3a3d4d] group-hover:bg-[#D4AF37]/60"
                }`}
              />
              <span
                className={`hidden xl:block absolute left-5 whitespace-nowrap font-mono text-[8px] tracking-[0.25em] transition-all duration-500 ${
                  i === active ? "text-[#D4AF37] opacity-100" : "text-[#475569] opacity-0 group-hover:opacity-70"
                }`}
              >
                {num} · {label}
              </span>
            </button>
          ))}
        </div>
        <span className="w-px h-10 bg-gradient-to-t from-transparent to-[#D4AF37]/35" />
      </nav>
    </>
  );
}
